import { forwardRef } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import Link from '@mui/material/Link';
import Box, { BoxProps } from '@mui/material/Box';
// routes
import { RouterLink } from 'src/routes/components';

// ----------------------------------------------------------------------

export interface LogoProps extends BoxProps {
  disabledLink?: boolean;
}

const Logo = forwardRef<HTMLDivElement, LogoProps>(
  ({ disabledLink = false, sx, ...other }, ref) => {
    const theme = useTheme();

    const PRIMARY_LIGHT = theme.palette.primary.light;

    const PRIMARY_MAIN = theme.palette.primary.main;

    const PRIMARY_DARK = theme.palette.primary.dark;

    // OR using local (public folder)
    // -------------------------------------------------------
    // const logo = (
    //   <Box
    //     component="img"
    //     src="/logo/logo_single.svg" => your path
    //     sx={{ width: 40, height: 40, cursor: 'pointer', ...sx }}
    //   />
    // );

    const logo = (
      <Box
        ref={ref}
        component="div"
        sx={{
          width: 200,
          height: 40,
          display: 'inline-flex',
          ...sx,
        }}
        {...other}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 789 137"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M167.912 79.872V45.888C167.912 35.136 173.288 29.76 184.04 29.76H209.672L216.104 34.752V42.24H184.616C182.696 42.24 181.736 43.2 181.736 45.12V80.64C181.736 82.56 182.696 83.52 184.616 83.52H216.104V91.008L209.672 96H184.04C173.288 96 167.912 90.624 167.912 79.872ZM227.818 79.872V45.888C227.818 35.136 233.194 29.76 243.946 29.76H265.834C276.586 29.76 281.962 35.136 281.962 45.888V79.872C281.962 90.624 276.586 96 265.834 96H243.946C233.194 96 227.818 90.624 227.818 79.872ZM241.642 80.64C241.642 82.56 242.602 83.52 244.522 83.52H265.258C267.178 83.52 268.138 82.56 268.138 80.64V45.12C268.138 43.2 267.178 42.24 265.258 42.24H244.522C242.602 42.24 241.642 43.2 241.642 45.12V80.64ZM296.747 96V29.76H321.899C339.947 29.76 348.971 38.784 348.971 56.832V68.928C348.971 86.976 339.947 96 321.899 96H296.747ZM310.571 83.52H322.667C330.987 83.52 335.147 79.36 335.147 71.04V54.72C335.147 46.4 330.987 42.24 322.667 42.24H310.571V83.52ZM362.841 96V29.76H408.153V42.24H376.665V55.68H405.273V68.16H376.665V83.52H408.153V96H362.841ZM443.841 96V29.76H489.153V42.24H457.665V55.68H486.273V68.16H457.665V83.52H489.153V96H443.841ZM501.778 96V29.76H516.754L543.058 73.248V29.76H556.882V96H541.906L515.602 52.512V96H501.778ZM571.693 79.872V45.888C571.693 35.136 577.069 29.76 587.821 29.76H615.565L621.997 34.752V42.24H588.397C586.477 42.24 585.517 43.2 585.517 45.12V80.64C585.517 82.56 586.477 83.52 588.397 83.52H610.093V68.16H597.421V55.68H623.917V96H587.821C577.069 96 571.693 90.624 571.693 79.872ZM637.716 96V29.76H651.54V96H637.716ZM667.247 96V29.76H682.223L708.527 73.248V29.76H722.351V96H707.375L681.071 52.512V96H667.247ZM738.122 96V29.76H783.434V42.24H751.946V55.68H780.554V68.16H751.946V83.52H783.434V96H738.122Z"
            fill="white"
          />
          <rect width="137" height="137" rx="15" fill="url(#paint0_linear_50_94)" />
          <path
            d="M19.304 69.28V64.6L34.064 57.544L35.864 61.324L23.876 66.94L35.864 72.556L34.064 76.336L19.304 69.28ZM40.4045 72.952V60.208C40.4045 56.176 42.4205 54.16 46.4525 54.16H56.0645L58.4765 56.032V58.84H46.6685C45.9485 58.84 45.5885 59.2 45.5885 59.92V73.24C45.5885 73.96 45.9485 74.32 46.6685 74.32H58.4765V77.128L56.0645 79H46.4525C42.4205 79 40.4045 76.984 40.4045 72.952ZM60.9973 79.576L71.0773 52.072L75.2533 53.584L65.1733 81.088L60.9973 79.576ZM78.909 79V54.16H95.901V58.84H84.093V63.88H94.821V68.56H84.093V74.32H95.901V79H78.909ZM99.6276 72.556L111.616 66.94L99.6276 61.324L101.428 57.544L116.188 64.6V69.28L101.428 76.336L99.6276 72.556Z"
            fill="#10344F"
          />
          <defs>
            <linearGradient
              id="paint0_linear_50_94"
              x1="81.2301"
              y1="1.03968e-06"
              x2="56.073"
              y2="137"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#A2FFF0" />
              <stop offset="1" stopColor="#2CF1BE" />
            </linearGradient>
          </defs>
        </svg>
      </Box>
    );

    if (disabledLink) {
      return logo;
    }

    return (
      <Link component={RouterLink} href="/" sx={{ display: 'contents' }}>
        {logo}
      </Link>
    );
  }
);

export default Logo;
